import { ArrowDown, ArrowUp, ArrowUpRight, Minus } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { MetricLabel } from '@/components/finance/MetricLabel'
import { cogsBasisDisplay } from '@/components/finance/cogsBasisDisplay'
import { metricPairOrEmpty } from '@/components/admin/dashboard/dashboardFormat'
import { useCurrency } from '@/contexts/CurrencyContext'
import { formatMoneyFixed } from '@/lib/currency'
import type { MetricKey } from '@/lib/financeGlossary'
import { cn } from '@/lib/utils'
import type { IntMetricPair, MetricPair, OverviewReport } from '@/types'

/**
 * The Reports overview headline (F-FE-N204) — a gateway, not a wall of tiles.
 * Four figures in the same words as the Dashboard and the P&L (Sales, Orders,
 * Avg order, Profit), then one quiet row of the parts of the money. Every
 * figure opens the report behind it for the same period.
 */

export type ReportTarget = 'daily' | 'adjustments' | 'orders' | 'items' | 'pnl' | 'tax' | 'refunds' | 'ledger' | 'tables'

export const TARGET_LABEL: Record<ReportTarget, string> = {
  daily: 'Daily sales',
  adjustments: 'Discounts & service charge',
  orders: 'Orders',
  items: 'Items',
  pnl: 'P&L',
  tax: 'Tax',
  refunds: 'Refund logs',
  ledger: 'Expense ledger',
  tables: 'Tables & parties',
}

interface Props {
  overview: OverviewReport
  /** "vs Fri", "vs last month" — short, beside the change. */
  comparisonShort: string
  /** The full comparison window, for the hover text. */
  comparisonFull: string
  onOpen: (target: ReportTarget) => void
}

/** Has the tenant recorded guest counts in either window? 0 and 0 means it never does. */
export function recordsGuests(covers: IntMetricPair): boolean {
  return covers.current > 0 || covers.previous > 0
}

/** Profit as a share of sales after tax, the P&L's yardstick; null with no sales. */
export function keptShare(netProfit: number, netSales: number): number | null {
  return netSales > 0.005 ? (netProfit / netSales) * 100 : null
}

export function ReportsHeadline({ overview, comparisonShort, comparisonFull, onOpen }: Props) {
  const { formatCurrency, currencyCode } = useCurrency()
  const whole = (n: number) => formatMoneyFixed(n, currencyCode, { digits: 0 })
  const count = (n: number) => n.toLocaleString('en-US')

  const sales = metricPairOrEmpty(overview.gross_sales_after_refunds)
  const refunds = metricPairOrEmpty(overview.refunds_issued)
  const profit = metricPairOrEmpty(overview.net_profit)
  const basis = cogsBasisDisplay(overview.cogs_basis)
  const coverage = metricPairOrEmpty(overview.projected_coverage_pct).current
  const kept = keptShare(profit.current, overview.net_sales.current)

  const profitNote = (
    <>
      {kept == null ? 'No sales to measure against' : kept < 0 ? `${Math.abs(kept).toFixed(1)}% of sales lost` : `${kept.toFixed(1)}% of sales kept`}
      {basis.showCoverage && coverage < 99.5 && (
        <span className={coverage < 50 ? 'text-amber-600 dark:text-amber-400' : undefined}>
          {' '}· costs set on {coverage.toFixed(0)}% of sales
        </span>
      )}
    </>
  )

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <HeadCard
          label="Sales"
          metric="gross_sales_after_refunds"
          value={whole(sales.current)}
          exact={formatCurrency(sales.current)}
          pair={sales}
          previous={formatCurrency(sales.previous)}
          comparisonShort={comparisonShort}
          comparisonFull={comparisonFull}
          target="daily"
          onOpen={onOpen}
          note={
            refunds.current > 0.005 ? (
              <>
                {whole(overview.gross_sales.current)} sold ·{' '}
                <span className="font-medium text-rose-600">{whole(refunds.current)} refunded</span>
              </>
            ) : undefined
          }
        />
        <HeadCard
          label="Orders"
          metric="orders"
          value={count(overview.orders.current)}
          pair={overview.orders}
          previous={count(overview.orders.previous)}
          comparisonShort={comparisonShort}
          comparisonFull={comparisonFull}
          target="orders"
          onOpen={onOpen}
        />
        <HeadCard
          label="Avg order"
          metric="avg_ticket"
          value={whole(overview.average_check.current)}
          exact={formatCurrency(overview.average_check.current)}
          pair={overview.average_check}
          previous={formatCurrency(overview.average_check.previous)}
          comparisonShort={comparisonShort}
          comparisonFull={comparisonFull}
          target="items"
          onOpen={onOpen}
        />
        <HeadCard
          label="Profit"
          metric="net_profit"
          value={whole(profit.current)}
          exact={formatCurrency(profit.current)}
          pair={profit}
          previous={formatCurrency(profit.previous)}
          comparisonShort={comparisonShort}
          comparisonFull={comparisonFull}
          target="pnl"
          onOpen={onOpen}
          negative={profit.current < -0.005}
          note={profitNote}
          emphasised
        />
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-[repeat(auto-fit,minmax(9.5rem,1fr))]">
        <PartLink label="Sales tax" metric="sales_tax" value={whole(overview.tax.current)} target="tax" onOpen={onOpen} />
        <PartLink label="Service charge" metric="service_charge" value={whole(overview.service_charge.current)} target="adjustments" onOpen={onOpen} />
        <PartLink label="Discounts" metric="discounts" value={whole(overview.discounts.current)} target="adjustments" onOpen={onOpen} />
        <PartLink label="Refunds" metric="refunds_issued" value={whole(refunds.current)} target="refunds" onOpen={onOpen} />
        <PartLink
          label="Cost of goods"
          metric={basis.costMetric}
          value={whole(metricPairOrEmpty(overview.projected_cogs).current)}
          target="pnl"
          onOpen={onOpen}
        />
        <PartLink
          label="Operating expenses"
          metric="operating_expenses"
          value={whole(metricPairOrEmpty(overview.operating_expenses).current)}
          target="ledger"
          onOpen={onOpen}
        />
        {recordsGuests(overview.covers) && (
          <PartLink label="Guests" metric="covers" value={count(overview.covers.current)} target="tables" onOpen={onOpen} />
        )}
      </div>
    </div>
  )
}

function Change({ pair, invert = false }: { pair: MetricPair | IntMetricPair; invert?: boolean }) {
  if (pair.pct == null) return <span className="text-muted-foreground">—</span>
  if (pair.delta === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-muted-foreground">
        <Minus className="h-3 w-3" />
        0%
      </span>
    )
  }
  const up = pair.delta > 0
  const good = up !== invert
  const Icon = up ? ArrowUp : ArrowDown
  return (
    <span className={cn('inline-flex items-center gap-0.5 font-medium', good ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400')}>
      <Icon className="h-3 w-3" />
      {Math.abs(pair.pct).toFixed(0)}%
    </span>
  )
}

function HeadCard({
  label,
  metric,
  value,
  exact,
  pair,
  previous,
  comparisonShort,
  comparisonFull,
  target,
  onOpen,
  note,
  negative = false,
  emphasised = false,
}: {
  label: string
  metric: MetricKey
  value: string
  exact?: string
  pair: MetricPair | IntMetricPair
  previous: string
  comparisonShort: string
  comparisonFull: string
  target: ReportTarget
  onOpen: (t: ReportTarget) => void
  note?: React.ReactNode
  negative?: boolean
  emphasised?: boolean
}) {
  const open = () => onOpen(target)
  return (
    <Card
      role="button"
      tabIndex={0}
      aria-label={`${label}: ${value}. Open ${TARGET_LABEL[target]}`}
      onClick={open}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          open()
        }
      }}
      className={cn(
        'group cursor-pointer transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        emphasised && 'border-primary/30 bg-primary/5',
      )}
    >
      <CardContent className="space-y-2 p-5">
        <div className="flex items-center justify-between gap-2">
          <MetricLabel metric={metric} label={label} className="text-sm font-medium text-muted-foreground" />
          <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
            {TARGET_LABEL[target]}
            <ArrowUpRight className="h-3.5 w-3.5" />
          </span>
        </div>
        <p className={cn('text-3xl font-semibold tracking-tight', negative ? 'text-red-600 dark:text-red-400' : 'text-foreground')} title={exact}>
          {value}
        </p>
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground" title={`${comparisonFull}: ${previous}`}>
          <Change pair={pair} />
          <span className="truncate">{comparisonShort}</span>
        </p>
        {note && <p className="truncate text-xs text-muted-foreground">{note}</p>}
      </CardContent>
    </Card>
  )
}

function PartLink({
  label,
  value,
  target,
  onOpen,
}: {
  label: string
  /** Glossary key — kept for parity with the head cards; the row is plain text. */
  metric?: MetricKey
  value: string
  target: ReportTarget
  onOpen: (t: ReportTarget) => void
}) {
  return (
    <button
      type="button"
      onClick={() => onOpen(target)}
      title={`Open ${TARGET_LABEL[target]}`}
      className="group flex flex-col items-start gap-1 rounded-lg border bg-card px-4 py-3 text-left transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span className="flex w-full items-center justify-between gap-2 text-xs text-muted-foreground">
        <span className="truncate">{label}</span>
        <ArrowUpRight className="h-3.5 w-3.5 shrink-0 opacity-40 transition-opacity group-hover:opacity-100" />
      </span>
      <span className="text-lg font-semibold tabular-nums text-foreground">{value}</span>
    </button>
  )
}
